import {useState} from 'react';
import styled from 'styled-components';
import {graphql, ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import type {ProfileRouteQuery as ProfileRouteQueryType} from './__generated__/ProfileRouteQuery.graphql';
import {useAuth} from '../context/AuthContext';
import {useUpdateProfile} from '../hooks/useUpdateProfile';

const Container = styled.div`
	margin: 0 auto;
	max-width: 48rem;
	padding: 24px;
`;
const PageTitle = styled.h1`
	font-size: 24px;
	line-height: 32px;
	font-weight: 700;
	color: #0f172a;
	margin: 0;
`;
const Section = styled.section`
	margin-top: 24px;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 16px;
`;
const SectionTitle = styled.h2`
	font-size: 18px;
	line-height: 28px;
	font-weight: 600;
	color: #0f172a;
	margin: 0;
`;
const SectionText = styled.p`
	margin: 4px 0 0;
	font-size: 14px;
	line-height: 20px;
	color: #64748b;
`;
const IdentityList = styled.dl`
	margin: 12px 0 0;
	display: grid;
	grid-template-columns: 8rem 1fr;
	row-gap: 8px;
	font-size: 14px;
	line-height: 20px;
`;
const IdentityTerm = styled.dt`
	font-weight: 500;
	color: #64748b;
`;
const IdentityValue = styled.dd`
	color: #0f172a;
	margin: 0;
`;
const SignatureForm = styled.form`
	margin-top: 12px;
	> * + * {
		margin-top: 12px;
	}
`;
const SrOnlyLabel = styled.label`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;
const SignatureTextarea = styled.textarea`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 8px 12px;
	font-size: 14px;
	line-height: 20px;
	&:focus {
		outline: none;
		border-color: #3b82f6;
		box-shadow: 0 0 0 1px #3b82f6;
	}
`;
const EmailInput = styled.input`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 8px 12px;
	font-size: 14px;
	line-height: 20px;
	&:focus {
		outline: none;
		border-color: #3b82f6;
		box-shadow: 0 0 0 1px #3b82f6;
	}
`;
const SavedText = styled.p`
	font-size: 14px;
	line-height: 20px;
	color: #15803d;
	margin: 0;
`;
const FooterRow = styled.div`
	display: flex;
	justify-content: flex-end;
`;
const SaveButton = styled.button`
	border-radius: 4px;
	background: #1d4ed8;
	padding: 8px 20px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #ffffff;
	border: 0;
	cursor: pointer;
	&:hover {
		background: #1e40af;
	}
	&:disabled {
		opacity: 0.5;
	}
`;
const ErrorText = styled.p`
	margin-top: 16px;
	font-size: 14px;
	line-height: 20px;
	color: #dc2626;
`;
const Loading = styled.div`
	padding: 16px;
	color: #64748b;
`;

const ProfileRouteQueryDoc = graphql`
	query ProfileRouteQuery {
		viewer {
			id
			ticketingEmailSignature
			ticketingReplyToEmail
		}
	}
`;

function isValidEmail(value: string): boolean {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function ProfileForm({initialSignature, initialReplyToEmail}: {initialSignature: string; initialReplyToEmail: string}) {
	const {user} = useAuth();
	const {saveSignature, savingSignature, saveReplyToEmail, savingReplyToEmail, error} = useUpdateProfile();

	const [signature, setSignature] = useState(initialSignature);
	const [savedSignature, setSavedSignature] = useState(false);

	const [replyToEmail, setReplyToEmail] = useState(initialReplyToEmail);
	const [savedReplyToEmail, setSavedReplyToEmail] = useState(false);
	const [replyToEmailError, setReplyToEmailError] = useState<string | null>(null);

	async function handleSaveSignature(e: React.FormEvent) {
		e.preventDefault();
		setSavedSignature(false);
		try {
			await saveSignature(signature.length > 0 ? signature : null);
			setSavedSignature(true);
		} catch {
			// error surfaced by the hook
		}
	}

	async function handleSaveReplyToEmail(e: React.FormEvent) {
		e.preventDefault();
		setSavedReplyToEmail(false);
		setReplyToEmailError(null);
		if (replyToEmail.length > 0 && !isValidEmail(replyToEmail)) {
			setReplyToEmailError('Enter a valid email address.');
			return;
		}
		try {
			await saveReplyToEmail(replyToEmail.length > 0 ? replyToEmail : null);
			setSavedReplyToEmail(true);
		} catch {
			// error surfaced by the hook
		}
	}

	return (
		<Container>
			<PageTitle>Ticket preferences</PageTitle>

			{/* Read-only Forecast identity */}
			<Section>
				<SectionTitle>Profile</SectionTitle>
				<SectionText>Your name and email are managed in Forecast and shown here for reference.</SectionText>
				<IdentityList>
					<IdentityTerm>Name</IdentityTerm>
					<IdentityValue>{user?.name}</IdentityValue>
					<IdentityTerm>Email</IdentityTerm>
					<IdentityValue>{user?.email}</IdentityValue>
				</IdentityList>
			</Section>

			{/* Email signature */}
			<Section>
				<SectionTitle>Email signature</SectionTitle>
				<SectionText>Appended to your outbound ticket replies.</SectionText>
				<SignatureForm onSubmit={handleSaveSignature}>
					<SrOnlyLabel htmlFor="email_signature">Email signature</SrOnlyLabel>
					<SignatureTextarea
						id="email_signature"
						value={signature}
						onChange={e => {
							setSignature(e.target.value);
							setSavedSignature(false);
						}}
						rows={5}
						placeholder="e.g. Best regards, Ada"
					/>
					{savedSignature && <SavedText role="status">Saved</SavedText>}
					<FooterRow>
						<SaveButton type="submit" disabled={savingSignature}>
							{savingSignature ? 'Saving…' : 'Save signature'}
						</SaveButton>
					</FooterRow>
				</SignatureForm>
			</Section>

			{/* Reply-To address */}
			<Section>
				<SectionTitle>Reply-To address</SectionTitle>
				<SectionText>
					{"Customer replies to your messages go here. Leave blank to use the ticket's address."}
				</SectionText>
				<SignatureForm onSubmit={handleSaveReplyToEmail} noValidate>
					<SrOnlyLabel htmlFor="reply_to_email">Reply-To address</SrOnlyLabel>
					<EmailInput
						id="reply_to_email"
						type="email"
						value={replyToEmail}
						onChange={e => {
							setReplyToEmail(e.target.value);
							setSavedReplyToEmail(false);
							setReplyToEmailError(null);
						}}
						onBlur={() => {
							if (replyToEmail.length > 0 && !isValidEmail(replyToEmail)) {
								setReplyToEmailError('Enter a valid email address.');
							}
						}}
						placeholder="e.g. you@example.com"
						aria-describedby={replyToEmailError ? 'reply_to_email_error' : undefined}
					/>
					{replyToEmailError && (
						<ErrorText id="reply_to_email_error" role="alert">
							{replyToEmailError}
						</ErrorText>
					)}
					{savedReplyToEmail && <SavedText role="status">Saved</SavedText>}
					<FooterRow>
						<SaveButton type="submit" disabled={savingReplyToEmail}>
							{savingReplyToEmail ? 'Saving…' : 'Save address'}
						</SaveButton>
					</FooterRow>
				</SignatureForm>
			</Section>

			{error && <ErrorText>{error}</ErrorText>}
		</Container>
	);
}

export function ProfileRoute() {
	return (
		<ForecastQueryRenderer
			query={ProfileRouteQueryDoc}
			variables={{}}
			customLoader={() => <Loading>Loading…</Loading>}
			redirectIfNotLoggedIn
			render={(relayProps: ProfileRouteQueryType['response'] | null) => {
				if (!relayProps || !relayProps.viewer) return null;
				const v = relayProps.viewer;
				return (
					<ProfileForm
						initialSignature={v.ticketingEmailSignature ?? ''}
						initialReplyToEmail={v.ticketingReplyToEmail ?? ''}
					/>
				);
			}}
		/>
	);
}
